import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { UsageIndicator } from './UsageIndicator';
import { TierBadge } from './TierBadge';
import type { SubscriptionTier, ActionType } from '@/types';
import { ExclamationTriangleIcon } from '@radix-ui/react-icons';

interface TierLimitDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  actionType: ActionType;
  subscriptionTier: SubscriptionTier;
  onUpgrade?: () => void;
}

export function TierLimitDialog({
  isOpen,
  onOpenChange,
  actionType,
  subscriptionTier,
  onUpgrade
}: TierLimitDialogProps) {
  const [isUpgrading, setIsUpgrading] = useState(false);

  const actionLabels = {
    purchases: 'purchase',
    listings: 'listing',
    items: 'item'
  };

  const handleUpgrade = async () => {
    if (!onUpgrade) return;

    setIsUpgrading(true);
    try {
      await onUpgrade();
    } catch (error) {
      console.error('Upgrade failed:', error);
    } finally {
      setIsUpgrading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/20">
              <ExclamationTriangleIcon className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div className="space-y-1">
              <DialogTitle className="text-left">
                {subscriptionTier === 'FREE' ? 'Free tier limit reached' : 'Subscription limit reached'}
              </DialogTitle>
              <TierBadge tier={subscriptionTier} className="w-fit" />
            </div>
          </div>
          <DialogDescription className="text-left">
            You've reached your limit for {actionLabels[actionType]}s.
            {subscriptionTier === 'FREE'
              ? ' Upgrade to continue adding new items and unlock unlimited storage.'
              : ' Please check your subscription status or contact support.'
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-muted-foreground">Current Usage</h4>
            <UsageIndicator
              actionType="purchases"
              subscriptionTier={subscriptionTier}
              size="sm"
            />
            <UsageIndicator
              actionType="listings"
              subscriptionTier={subscriptionTier}
              size="sm"
            />
            <UsageIndicator
              actionType="items"
              subscriptionTier={subscriptionTier}
              size="sm"
            />
          </div>

          {subscriptionTier === 'FREE' && (
            <div className="p-3 bg-blue-50 dark:bg-blue-900/10 rounded-lg border border-blue-200 dark:border-blue-800">
              <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-1">
                Upgrade to Active Plan
              </h4>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Get unlimited storage and access to all premium features.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Continue with current data
          </Button>
          {subscriptionTier === 'FREE' && onUpgrade && (
            <Button
              onClick={handleUpgrade}
              disabled={isUpgrading}
            >
              {isUpgrading ? 'Upgrading...' : 'Upgrade Now'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}